/**
 * Escape Meta (Facebook / Instagram) in-app webviews.
 *
 * Those embedded browsers don't support Google OAuth, so traffic from Meta ads
 * must be bounced out to the system browser (Safari / Chrome). Behavior is
 * gated by URL params so it only runs for the intended funnels:
 *   ?bb=true         → redirect on iOS and Android
 *   ?bb_android=true → redirect on Android only
 */

const isBrowser = typeof window !== 'undefined';

type OS = 'ios' | 'android' | 'mac' | 'windows' | 'linux' | 'other';

function uaIncludes(token: string): boolean {
  if (!isBrowser) return false;
  try {
    return window.navigator.userAgent.includes(token);
  } catch {
    return false;
  }
}

export const isInstagram = uaIncludes('Instagram');
export const isFacebook = uaIncludes('FBAV') || uaIncludes('FBAN');
export const isMetaInAppBrowser = isInstagram || isFacebook;

/** Coarse device class from the UA — for the analytics `device_type` param. */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (!isBrowser) return 'desktop';
  const ua = window.navigator.userAgent;
  if (/iPad/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/.test(ua)) return 'mobile';
  return 'desktop';
}

export function getOS(): OS {
  if (!isBrowser) return 'other';
  const ua = window.navigator.userAgent;
  const platform = window.navigator.platform;

  if (['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'].includes(platform)) return 'mac';
  if (['iPhone', 'iPad', 'iPod'].includes(platform)) return 'ios';
  if (['Win32', 'Win64', 'Windows', 'WinCE'].includes(platform)) return 'windows';
  if (/Android/.test(ua)) return 'android';
  if (/Linux/.test(platform)) return 'linux';
  return 'other';
}

/** True when the device iOS major version is <= `version`. */
function isIOSVersionAtMost(version: number): boolean {
  if (!isBrowser) return false;
  try {
    const ua = navigator.userAgent || navigator.vendor;
    if (!/iPad|iPhone|iPod|Macintosh|Mac/i.test(ua) || (window as unknown as { MSStream?: unknown }).MSStream) {
      return false;
    }
    const match = (ua.match(/\b[0-9]+_[0-9]+(?:_[0-9]+)?\b/) || [''])[0];
    const major = Number(match.split('_')[0]);
    return Number.isFinite(major) && major > 0 && major <= version;
  } catch {
    return false;
  }
}

const ANDROID_REDIRECT = 'https://browser.redirectnative.xyz/redirect.php?url=';
const IOS14_REDIRECT = 'ftp://files.devroqapps.com/redirect.html?url=';

function openInSystemBrowser(): void {
  if (!isBrowser) return;
  const os = getOS();
  const target = encodeURIComponent(window.location.href);

  if (isInstagram && os === 'ios') {
    window.location.href = `instagram://extbrowser/?url=${target}`;
    return;
  }

  switch (os) {
    case 'ios':
      if (isIOSVersionAtMost(14)) {
        window.location.href = `${IOS14_REDIRECT}${target}`;
      } else if (isIOSVersionAtMost(15)) {
        // iOS 15: no reliable scheme — leave the user in place.
      } else if (isIOSVersionAtMost(16)) {
        window.location.href = `googlechrome-x-callback://x-callback-url/open/?url=${target}`;
      } else {
        window.location.href = `x-safari-${window.location.href}`;
      }
      break;
    case 'android':
      window.location.href = `${ANDROID_REDIRECT}${target}`;
      break;
  }
}

/**
 * Bounce out of a Meta in-app browser when the funnel params request it.
 * Params are injected (config + URL merged) to keep this module framework-free.
 *   bb=true         → redirect on iOS and Android
 *   bb_android=true → redirect on Android only
 */
export function maybeRedirectToNativeBrowser(params: Record<string, string>): void {
  if (!isBrowser || !isMetaInAppBrowser) return;

  if (params.bb === 'true') {
    openInSystemBrowser();
  } else if (params.bb_android === 'true' && getOS() === 'android') {
    openInSystemBrowser();
  }
}
